import React, {useContext} from "react";
import {TypeImageSkeleton} from "@/libs/contentful/types";
import Text from '@/components/typographie/Text/Text'
import Headline from '@/components/typographie/Headlines/Headline'
import NavLink from "@/components/Layout/Header/Navigation/NavLink";
import IconRenderer from "@/libs/contentful/helper/IconRenderer";
import {MegaMenuContext} from "@/components/Layout/Header/Navigation/MainNavProvider";
import clsx from "clsx";
import {usePathname} from "next/navigation";
import {useTranslations} from "next-intl";
import contentfulImage from "@/libs/contentful/helper/ContentfulImage";
import {isActiveNavPath} from "@/libs/helper/helper";

interface Props {
  id: string
  url: string
  introHeadline: string | null | undefined
  introText: string | null | undefined
  introImage: TypeImageSkeleton | null | undefined
  subPages?: {
    id: string,
    page: {
      url: {
        values: string
      }
    },
    title: {
      values: string
    }
  }[]
}

export default function MegaMenu({id, url, introHeadline, introText, introImage, subPages}: Props) {
  const {isMegaMenuOpen } = useContext(MegaMenuContext);
  const urlPattern = /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
  const t = useTranslations();
  const pathname = usePathname();
  const imageUrl: string = contentfulImage(introImage?.fields.default?.fields, {
    quality: 80,
    format: "webp",
    width : 233,
    height : 335,
    fit: 'fill'
  });
  return (
      <div id={id}
           role="dialog"
           className={clsx(!isMegaMenuOpen ? 'h-[0] invisible overflow-hidden md:opacity-0 md:pointer-events-none' : '', "mega-menu brand-transition w-full bg-brand-light-cool-grey md:absolute md:left-[0] md:h-auto md:bg-white md:px-2xl md:py-3xl md:z-10 lg:px-3xl")}>
        <ul className="pl-[0] list-none my-[0] w-full md:flex-wrap md:justify-between md:flex md:h-full">
          <li key={introHeadline} className="px-4 w-full md:block md:w-2/3 lg:w-1/2">
            <div className="flex h-full">
              <img src={imageUrl} className={'w-fill h-full object-cover hidden w-max max-w-[233px] md:block'} width={233}
                   height={335} loading="lazy"/>
              <div className="w-full md:mx-m md:flex md:max-w-sm md:flex-col md:items-start">
                <div className="hidden md:block">
                  {introHeadline && (<Headline>{introHeadline}</Headline>)}
                  {introText && (<Text className="mt-s">{introText}</Text>)}
                </div>
                {url && <NavLink
                    className="group/focus focus:outline-0 md:focus:outline-2 brand-transition block w-full px-2xl py-m text-link font-normal text-black md:mt-auto md:flex md:w-fit md:rounded md:border md:border-black md:py-s md:font-bonprix md:text-above md:hover:bg-black md:hover:text-white md:focus:bg-black md:focus:text-white"
                    slug={url}
                    ariaLabel={t("Defaults.navigatesTo") + ' ' + introHeadline + ' ' + t("Defaults.page")}
                >
                  <span className="group-focus/focus:outline-offset-[5px] group-focus/focus:outline-2 md:group-focus/focus:outline-0 group-focus/focus:outline-outline group-focus/focus:outline">
                    {t('MainMenu.overview')}
                  </span>
                </NavLink>}
              </div>
            </div>
          </li>
          <div className="px-4 w-full md:w-1/3 lg:w-1/2">
            {subPages && subPages.map((subNavItem) => {
              const resolvedSubUrl = urlPattern.test(subNavItem.page.url.values) ? subNavItem.page.url.values : url + subNavItem.page.url.values;
              const isSubActive = isActiveNavPath(pathname, resolvedSubUrl);
              return subNavItem.page.url?.values && <li
                    key={subNavItem.id}
                    className={clsx('brand-transition group/arrow md:hover:bg-brand-hover', isSubActive ? 'md:border-b-2 md:border-black' : 'md:border-b md:border-brand-light-cool-grey')}
                >
                  <NavLink
                      className="flex justify-between px-2xl py-mobile text-link font-normal brand-transition md:focus:bg-brand-hover md:px-xl md:font-semibold group/focus focus:outline-0 md:focus:outline-2 text-black"
                      slug={resolvedSubUrl}
                      ariaLabel={t("Defaults.navigatesTo") + ' ' + subNavItem.title.values + ' ' + t("Defaults.page")}>
                        <span className={clsx("group-focus/focus:outline-offset-[5px] group-focus/focus:outline-2 md:group-focus/focus:outline-0 group-focus/focus:outline-outline group-focus/focus:outline", isSubActive ? 'border-b-2 md:border-none' : null)}>
                          {subNavItem.title.values}
                        </span>
                    <IconRenderer icon="icon-arrow"
                                  className="right-[20px] hidden w-[24px] -translate-x-s opacity-0 brand-transition group-focus-within/arrow:translate-x-[0] group-focus-within/arrow:opacity-100 group-hover/arrow:translate-x-[0] group-hover/arrow:opacity-100 md:block"/>
                  </NavLink>
                </li>
            })}
          </div>
        </ul>
      </div>
  )
}
